import React, { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { atsCheck } from '../../lib/api';
import { Btn, C, Card, ModelChip, Stepper } from './ui';

// Step ① Upload: the job and the resume file. The name comes from the
// resume itself, and every resume runs on Opus 5.5, so there's nothing else
// to fill in.

export type AtsJob = { _id: string; title: string; company: string; description: string };
export type JobDraft = { jobId: string; jobTitle: string; company: string; jd: string };
export const EMPTY_JOB: JobDraft = { jobId: '', jobTitle: '', company: '', jd: '' };

const MAX_BYTES = 5 * 1024 * 1024;
const JD_MIN = 80;
const input = 'w-full px-3.5 py-2.5 rounded-xl text-sm bg-gray-50/80 border border-gray-200 focus:outline-none focus:bg-white focus:border-purple-400 focus:ring-4 focus:ring-purple-100 transition-all';

export function jobProblem(j: JobDraft) {
  if (!j.jobTitle.trim()) return 'Enter the job title';
  if (j.jd.trim().length < JD_MIN) return 'Paste the full job description';
  return '';
}

export const JobFields: React.FC<{ jobs: AtsJob[]; value: JobDraft; onChange: (j: JobDraft) => void }> = ({ jobs, value, onChange }) => {
  const pick = (id: string) => {
    const j = jobs.find(x => x._id === id);
    onChange(j ? { jobId: j._id, jobTitle: j.title, company: j.company, jd: j.description } : { ...value, jobId: '' });
  };
  const short = value.jd.trim().length < JD_MIN;
  return (
    <div className="space-y-2.5">
      {jobs.length > 0 && (
        <select value={value.jobId} onChange={e => pick(e.target.value)} className={`${input} font-semibold`}>
          <option value="">📋 Pick a job from the Job Board (or type it below)</option>
          {jobs.map(j => <option key={j._id} value={j._id}>{j.title} — {j.company}</option>)}
        </select>
      )}
      <div className="grid sm:grid-cols-2 gap-2.5">
        <input value={value.jobTitle} onChange={e => onChange({ ...value, jobTitle: e.target.value })} placeholder="Job title *" className={input} />
        <input value={value.company} onChange={e => onChange({ ...value, company: e.target.value })} placeholder="Company" className={input} />
      </div>
      <textarea value={value.jd} onChange={e => onChange({ ...value, jd: e.target.value })} rows={8}
        placeholder="Paste the full job description *" className={`${input} resize-y leading-relaxed`} />
      <p className={`text-[11px] text-right ${short && value.jd ? 'text-amber-600 font-semibold' : 'text-gray-400'}`}>
        {value.jd.length.toLocaleString('en-IN')} characters{short && value.jd ? ` · at least ${JD_MIN}` : ''}
      </p>
    </div>
  );
};

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error("Couldn't read the file"));
    r.readAsDataURL(file);
  });
}

const sizeOf = (n: number) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const okType = (f: File) => /\.(pdf|docx)$/i.test(f.name);

const Dropzone: React.FC<{ file: File | null; onFile: (f: File | null) => void }> = ({ file, onFile }) => {
  const [over, setOver] = useState(false);
  const pickRef = useRef<HTMLInputElement>(null);
  const take = (f: File | undefined | null) => {
    if (!f) return;
    if (!okType(f)) { toast.error('Use a PDF or a Word (.docx) file'); return; }
    if (f.size > MAX_BYTES) { toast.error('The resume is too big. Use a file under 5 MB.'); return; }
    onFile(f);
  };
  return (
    <div
      onDragOver={e => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={e => { e.preventDefault(); setOver(false); take(e.dataTransfer.files?.[0]); }}
      onClick={() => pickRef.current?.click()}
      className={`relative cursor-pointer rounded-2xl border-2 border-dashed transition-all p-6 text-center ${over ? 'border-purple-500 bg-purple-50 scale-[1.01]' : file ? 'border-green-300 bg-green-50/50' : 'border-gray-200 bg-gray-50/60 hover:border-purple-300 hover:bg-purple-50/40'}`}>
      <input ref={pickRef} type="file" className="hidden" accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        onChange={e => { take(e.target.files?.[0]); e.target.value = ''; }} />
      {file ? (
        <div className="flex items-center gap-3 text-left ats-pop">
          <span className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl bg-white shadow-sm">{/\.pdf$/i.test(file.name) ? '📕' : '📘'}</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-extrabold text-gray-900 truncate">{file.name}</p>
            <p className="text-xs text-green-700 font-semibold">✓ Ready · {sizeOf(file.size)}</p>
          </div>
          <button type="button" onClick={e => { e.stopPropagation(); onFile(null); }} className="text-xs font-bold text-gray-400 hover:text-red-500 px-2">Remove</button>
        </div>
      ) : (
        <>
          <div className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center text-2xl text-white shadow-lg" style={{ background: `linear-gradient(135deg, ${C.purple}, ${C.purple2})` }}>⬆</div>
          <p className="text-sm font-extrabold text-gray-900 mt-3">Drop the resume here, or click to choose</p>
          <p className="text-xs text-gray-400 mt-1">PDF or Word (.docx) · up to 5 MB</p>
        </>
      )}
    </div>
  );
};

const Section: React.FC<{ n: number; title: string; hint?: string; children: React.ReactNode }> = ({ n, title, hint, children }) => (
  <div className="grid md:grid-cols-[180px_1fr] gap-3 md:gap-6">
    <div>
      <p className="flex items-center gap-2 text-sm font-extrabold text-gray-900">
        <span className="w-6 h-6 rounded-lg flex items-center justify-center text-[11px] font-black text-white" style={{ background: C.purple }}>{n}</span>
        {title}
      </p>
      {hint && <p className="text-[11px] text-gray-400 mt-1 md:pl-8 leading-snug">{hint}</p>}
    </div>
    <div className="min-w-0">{children}</div>
  </div>
);

const NewResume: React.FC<{
  pw: string; jobs: AtsJob[]; model: string;
  initial?: JobDraft; onStarted: (id: string) => void;
}> = ({ pw, jobs, model, initial, onStarted }) => {
  const [job, setJob] = useState<JobDraft>(initial ?? EMPTY_JOB);
  const [file, setFile] = useState<File | null>(null);
  const [sending, setSending] = useState(false);

  const submit = async () => {
    const why = jobProblem(job) || (!file ? 'Choose the resume file' : '');
    if (why) { toast.error(why); return; }
    setSending(true);
    try {
      const data = await readAsDataUrl(file!);
      const res = await atsCheck(pw, {
        jobTitle: job.jobTitle.trim(), company: job.company.trim(), jd: job.jd.trim(),
        jobId: job.jobId || undefined,
        resume: { data, fileName: file!.name },
      });
      onStarted(res.id);
    } catch (err: any) { toast.error(err.message); }
    finally { setSending(false); }
  };

  const ready = !jobProblem(job) && !!file;
  return (
    <div className="max-w-5xl mx-auto space-y-5 ats-fade">
      <Stepper at={1} />
      <Card pad={false}>
        <div className="p-5 sm:p-7 space-y-7">
          <div>
            <p className="text-xl font-black text-gray-900">New ATS resume</p>
            <p className="text-sm text-gray-500 mt-1">Give the job and the resume. The AI checks them first — nothing is changed until you say so.</p>
          </div>
          <Section n={1} title="The job" hint="From the Job Board, or paste any job.">
            <JobFields jobs={jobs} value={job} onChange={setJob} />
          </Section>
          <div className="h-px bg-gray-100" />
          <Section n={2} title="The resume" hint="The name and details are read from the file.">
            <Dropzone file={file} onFile={setFile} />
          </Section>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 sm:px-7 py-4 border-t border-gray-100 bg-gray-50/70 rounded-b-3xl">
          <div className="flex-1 flex flex-wrap items-center gap-2">
            {model && <ModelChip id={model} prefix="AI: " />}
            <p className="text-xs text-gray-500">🔒 Facts and education are never changed. Only the wording is, after you approve.</p>
          </div>
          <Btn kind="gold" size="lg" onClick={submit} disabled={sending} className={ready ? '' : 'opacity-70'}>
            {sending ? 'Uploading…' : 'Check resume →'}
          </Btn>
        </div>
      </Card>
    </div>
  );
};

export default NewResume;
